/**
 * Générateur — Lire et interpréter des informations présentées sous forme de
 * tableaux, de diagrammes et de graphiques.
 * Attendu ID : 973 | Niveau : 5ème | Domaine : Statistiques (descripteur 131)
 *
 * Version conforme à la spécification 973 (2026-09-21).
 * Supprimée : « Relier ou ne pas relier ? » (la pertinence de relier les points
 * d'une série discrète est un choix de REPRÉSENTATION, qu'aucun niveau de la
 * graduation n'appelle et dont aucun distracteur ne servait une des six erreurs
 * visées ; son axe à pas 1 sortait en outre de la liste des pas admis).
 * Ajoutées : les deux supports que l'objectif nomme et que le gen ne servait
 * pas — le tableau (simple entrée au niveau 1, double entrée au niveau 3) et le
 * graphique cartésien (niveau 3) — ainsi que l'encadrement d'une valeur entre
 * deux graduations (niveau 2).
 *
 * Plan de cartes :
 *   « Le tableau du cinéma » (niveau 1) — tableau à simple entrée, 4 à 6 jours,
 *     valeurs multiples de 25 jusqu'à 950 : lecture directe d'une valeur, écart
 *     entre deux valeurs, extremum, vrai/faux de comparaison.
 *   « La boutique de souvenirs » (niveau 2) — diagramme en barres, pas 50 €,
 *     aucune valeur écrite au sommet des barres, ventes sur les graduations :
 *     extremum (le plus / le moins), seuil (QCM multiple), relevé du tableau.
 *   « La piscine du quartier » (niveau 2) — diagramme en barres dont les
 *     sommets tombent STRICTEMENT entre deux graduations : encadrement de la
 *     fréquentation entre deux graduations consécutives (QCM et vrai/faux).
 *   « Les clubs du collège » (niveau 3) — tableau à double entrée (4 niveaux ×
 *     3 clubs) avec ligne et colonne de totaux, toutes les cases distinctes :
 *     lecture d'un croisement (saisie et QCM), vrai/faux de croisements.
 *   « La croissance du plant » (niveau 3) — graphique cartésien, temps en
 *     abscisse, hauteur en ordonnée, points sur les graduations : lecture dans
 *     les deux sens (hauteur d'une semaine, semaine d'une hauteur), puis
 *     interprétation (entre quelles semaines la hausse est la plus forte).
 *   « Paul et la télévision » (niveau 4) — DEUX diagrammes de la même série :
 *     A à axe tronqué (l'axe part de 15), B à axe complet. « souvent » vaut
 *     exactement le double de « parfois », mais le rapport apparent des
 *     hauteurs sur A dépasse 3,5 ; une catégorie est à 0 (pas de barre).
 *   « Tom et les deux villes » (niveau 4) — deux diagrammes circulaires de
 *     totaux différents : la part des sports collectifs est plus petite dans
 *     Bêta et l'effectif y est pourtant plus grand, par construction.
 *
 * Spécification appliquée :
 *   Bornes : modalités du document 4..8 (7 pour le tableau à double entrée :
 *     4 lignes + 3 colonnes) ; valeurs représentées 0..1000 ; pas de graduation
 *     pris dans {5, 10, 25, 50} avec 5 à 12 graduations sur l'axe ; écart
 *     minimal de 2 pas entre deux valeurs qu'une question compare ; 2 à 4
 *     formats (donc 2 à 4 questions) par document.
 *   Exclusions : aucune comparaison à moins de 2 pas d'écart ; aucune valeur
 *     sur une graduation quand l'encadrement est demandé (carte « piscine ») ;
 *     aucune valeur écrite au sommet des barres ; aucun ex æquo dans une
 *     question d'extremum ; axe tronqué au seul niveau 4, où la troncature est
 *     le sujet.
 *   Erreurs visées → mots-clés (`_meta.params.distracteurs[].code`) :
 *     - lire la graduation voisine au lieu d'encadrer → `graduation_voisine_pour_encadrement`
 *     - répondre sur le mauvais croisement d'un tableau → `ligne_pour_colonne`
 *     - désigner le plus grand quand on demande le plus petit → `extremum_inverse`
 *     - conclure d'un axe tronqué que l'écart réel est énorme → `axe_tronque_ecart_exagere`
 *     - comparer deux parts sans regarder les effectifs totaux → `parts_pour_effectifs`
 *     - projeter sur le mauvais axe d'un graphique → `axes_inverses`
 *   Graduation → cartes : 1 « Le tableau du cinéma » ; 2 « La boutique de
 *     souvenirs » et « La piscine du quartier » ; 3 « Les clubs du collège » et
 *     « La croissance du plant » ; 4 « Paul et la télévision » et « Tom et les
 *     deux villes ».
 *   Non tranché : la spec demande, pour une question d'interprétation, un choix
 *     binaire ACCOMPAGNÉ d'une justification rédigée dans un champ libre non
 *     corrigé ; aucun format TanoEd ne juxtapose une réponse corrigée et un
 *     champ libre : l'interprétation est servie en QCM dont chaque choix PORTE
 *     la justification. La variable « nombre de questions par document » est
 *     réalisée au niveau de la CARTE (2 à 4 formats sur le même document), un
 *     payload QCM ou saisie ne portant qu'une question. L'exclusion « aucune
 *     question dont la réponse est écrite sur le document » est lue à travers
 *     sa glose (étiquettes au sommet des barres) : sinon le niveau 1, qui
 *     demande la « lecture directe d'une valeur » d'un tableau, serait impossible.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import {
    imgElement, svgBarres, svgCamembert, svgLigneBrisee,
    ligneTable, ligneTableEntete, tableEnonce,
} from './svgStats';

const ATTENDU_ID = 973;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CINEMA = 'Le tableau du cinéma';
const L_BOUTIQUE = 'La boutique de souvenirs';
const L_PISCINE = 'La piscine du quartier';
const L_CROISE = 'Les clubs du collège';
const L_PLANT = 'La croissance du plant';
const L_TV = 'Paul et la télévision';
const L_VILLES = 'Tom et les deux villes';

/** Deux documents d'énoncé côte à côte (niveau 4 : un document et son jumeau). */
const deuxImages = (svgA, svgB) => ({
    version: 1,
    elements: [svgA, svgB].map((s) => ({
        type: 'image',
        config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(s)}`, maxWidth: 'small' },
    })),
});

/** Garantit qu'un tableau vrai/faux n'est ni tout vrai ni tout faux, sans boucle. */
const equilibrer = (vs) => {
    const nbVrai = vs.filter(Boolean).length;
    if (nbVrai === 0) vs[0] = true;
    else if (nbVrai === vs.length) vs[0] = false;
    return vs;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Le tableau du cinéma » (niveau 1 — tableau à simple entrée)
// ═════════════════════════════════════════════════════════════

const JOURS_CINEMA = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/** 4 à 6 jours, entrées multiples de 25 dans [75 ; 950], toutes distinctes ;
 *  maximum et minimum détachés d'au moins 50 entrées (extremums décidables). */
const tirerCinema = () => {
    for (let essai = 0; essai < 80; essai++) {
        const k = randInt(4, 6);
        const vals = [];
        for (let i = 0; i < k; i++) vals.push(randInt(3, 38) * 25);
        if (new Set(vals).size !== k) continue;
        const tri = [...vals].sort((a, b) => a - b);
        if (tri[k - 1] - tri[k - 2] < 50 || tri[1] - tri[0] < 50) continue;
        return { k, jours: JOURS_CINEMA.slice(0, k), vals, tri };
    }
    const vals = [225, 400, 150, 675, 325];
    return { k: 5, jours: JOURS_CINEMA.slice(0, 5), vals, tri: [...vals].sort((a, b) => a - b) };
};

const introCinema = (d) => '<p>Le tableau donne le nombre d\'entrées vendues par un cinéma '
    + 'chaque jour de la semaine dernière.</p>'
    + tableEnonce(
        ligneTableEntete(['Jour', ...d.jours]),
        ligneTable('Entrées vendues', d.vals),
    );

const paramsCinema = (d, extra = {}) => ({
    niveau: 1,
    support_du_document: 'tableau',
    nombre_de_modalites_du_document: d.k,
    valeur_representee: d.vals,
    nombre_de_questions_par_document: 3,
    ...extra,
});

const genCinemaSaisie = () => {
    const d = tirerCinema();
    if (randInt(0, 1) === 0) {
        const i = randInt(0, d.k - 1);
        const payload = makeSaisie(String(d.vals[i]),
            `${introCinema(d)}<p>Combien d'entrées le cinéma a-t-il vendues le ${d.jours[i]} ?</p>`,
            { ...opts(L_CINEMA), suffixe: 'entrées' });
        payload._meta.params = paramsCinema(d, { question: 'lecture directe' });
        return payload;
    }
    const iMax = d.vals.indexOf(d.tri[d.k - 1]);
    const iMin = d.vals.indexOf(d.tri[0]);
    const payload = makeSaisie(String(d.tri[d.k - 1] - d.tri[0]),
        `${introCinema(d)}<p>Combien d'entrées de plus le cinéma a-t-il vendues le ${d.jours[iMax]} `
        + `que le ${d.jours[iMin]} ?</p>`,
        { ...opts(L_CINEMA), suffixe: 'entrées' });
    payload._meta.params = paramsCinema(d, { question: 'comparaison de deux valeurs' });
    return payload;
};

const genCinemaQcm = () => {
    const d = tirerCinema();
    const plus = randInt(0, 1) === 0;
    const bonne = d.jours[d.vals.indexOf(plus ? d.tri[d.k - 1] : d.tri[0])];
    const inverse = d.jours[d.vals.indexOf(plus ? d.tri[0] : d.tri[d.k - 1])];
    const autres = shuffle(d.jours.filter((j) => j !== bonne && j !== inverse)).slice(0, 2);
    const payload = makeQcm([bonne, inverse, ...autres], bonne,
        `${introCinema(d)}<p>Quel jour le cinéma a-t-il vendu le ${plus ? 'plus' : 'moins'} d'entrées ?</p>`,
        opts(L_CINEMA));
    payload._meta.params = paramsCinema(d, {
        distracteurs: [{ code: 'extremum_inverse', valeur: inverse }],
    });
    return payload;
};

const genCinemaVF = () => {
    const d = tirerCinema();
    const iMax = d.vals.indexOf(d.tri[d.k - 1]);
    const iMin = d.vals.indexOf(d.tri[0]);
    const [a, b] = shuffle(d.jours.map((_, i) => i)).slice(0, 2);
    const [grand, petit] = d.vals[a] > d.vals[b] ? [d.jours[a], d.jours[b]] : [d.jours[b], d.jours[a]];
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('extremum_inverse');
    if (!vs[1]) codes.push('extremum_inverse');
    const lignes = [
        [`Le ${d.jours[iMax]}, le cinéma a vendu ${vs[0] ? 'le plus' : 'le moins'} d'entrées de la semaine.`, vs[0] ? 0 : 1],
        [`Le ${d.jours[iMin]}, le cinéma a vendu ${vs[1] ? 'le moins' : 'le plus'} d'entrées de la semaine.`, vs[1] ? 0 : 1],
        [`Le ${d.jours[a]}, le cinéma a vendu ${vs[2] ? d.vals[a] : d.vals[b]} entrées.`, vs[2] ? 0 : 1],
        [`Le cinéma a vendu plus d'entrées le ${vs[3] ? grand : petit} que le ${vs[3] ? petit : grand}.`, vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${introCinema(d)}<p>Vrai ou faux ?</p>`, opts(L_CINEMA));
    payload._meta.params = paramsCinema(d, { distracteurs: codes.map((code) => ({ code })) });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « La boutique de souvenirs » (niveau 2 — barres, extremum)
// ═════════════════════════════════════════════════════════════

const ARTICLES = ['Cartes', 'Habits', 'Bibelots', 'Gadgets', 'Montres'];
const PAS_BOUTIQUE = 50;

/** Ventes : multiples de 50 dans [150 ; 600], distinctes, maximum et minimum
 *  détachés d'au moins DEUX pas de graduation ; le seuil de la question à
 *  choix multiple est une graduation qui ne coïncide avec aucune barre. */
const tirerBoutique = () => {
    const candidats = [150, 200, 250, 300, 350, 400, 450, 500, 550, 600];
    for (let essai = 0; essai < 80; essai++) {
        const ventes = shuffle(candidats).slice(0, 5);
        const tri = [...ventes].sort((a, b) => a - b);
        if (tri[4] - tri[3] < 2 * PAS_BOUTIQUE || tri[1] - tri[0] < 2 * PAS_BOUTIQUE) continue;
        const seuils = candidats.filter((s) => s > tri[0] && s < tri[4] && !ventes.includes(s));
        if (!seuils.length) continue;
        const seuil = randomChoice(seuils);
        const auDessus = ventes.filter((v) => v > seuil).length;
        if (auDessus === 0 || auDessus === 5) continue;
        return { ventes, tri, seuil, yMax: tri[4], nGrad: tri[4] / PAS_BOUTIQUE };
    }
    const ventes = [200, 350, 450, 300, 600];
    return { ventes, tri: [...ventes].sort((a, b) => a - b), seuil: 400, yMax: 600, nGrad: 12 };
};

const svgBoutique = (d) => imgElement(svgBarres(ARTICLES, d.ventes,
    { yStep: PAS_BOUTIQUE, yLabel: 'Ventes (€)', xLabel: 'Types d\'articles' }));

const INTRO_BOUTIQUE = '<p>Le diagramme donne la répartition des ventes, en euros, d\'une boutique '
    + 'de souvenirs lors d\'une journée de juillet. Aucune valeur n\'est écrite au sommet des barres.</p>';

const paramsBoutique = (d, extra = {}) => ({
    niveau: 2,
    support_du_document: 'diagramme',
    famille_de_diagramme: 'diagramme en barres',
    nombre_de_modalites_du_document: ARTICLES.length,
    valeur_representee: d.ventes,
    pas_de_graduation_de_l_axe: PAS_BOUTIQUE,
    nombre_de_graduations_de_l_axe: d.nGrad,
    origine_de_l_axe_des_valeurs: 'zéro',
    position_de_la_valeur_par_rapport_aux_graduations: 'sur une graduation',
    ecart_minimal_entre_deux_valeurs_comparees: 2,
    nombre_de_questions_par_document: 3,
    ...extra,
});

const genBoutiqueQcm = () => {
    const d = tirerBoutique();
    const plus = randInt(0, 1) === 0;
    const bonne = ARTICLES[d.ventes.indexOf(plus ? d.tri[4] : d.tri[0])];
    const inverse = ARTICLES[d.ventes.indexOf(plus ? d.tri[0] : d.tri[4])];
    const autres = shuffle(ARTICLES.filter((a) => a !== bonne && a !== inverse)).slice(0, 2);
    const payload = makeQcm([bonne, inverse, ...autres], bonne,
        `${INTRO_BOUTIQUE}<p>Quel type d'article a rapporté le ${plus ? 'plus' : 'moins'} d'argent `
        + 'à la boutique ?</p>',
        opts(L_BOUTIQUE));
    payload.elements_additionnels = svgBoutique(d);
    payload._meta.params = paramsBoutique(d, {
        distracteurs: [{ code: 'extremum_inverse', valeur: inverse }],
    });
    return payload;
};

const genBoutiqueQcmMulti = () => {
    const d = tirerBoutique();
    const plus = randInt(0, 1) === 0;
    const corrects = ARTICLES.filter((a, i) => (plus ? d.ventes[i] > d.seuil : d.ventes[i] < d.seuil));
    const n = corrects.length;
    const payload = makeQcm(ARTICLES, corrects,
        `${INTRO_BOUTIQUE}<p>Quels types d'articles ont rapporté ${plus ? 'plus' : 'moins'} de ${d.seuil} € ? `
        + `Sélectionne ${n === 1 ? 'la bonne réponse' : `les ${n} bonnes réponses`}.</p>`,
        opts(L_BOUTIQUE), true);
    payload.elements_additionnels = svgBoutique(d);
    payload._meta.params = paramsBoutique(d, { seuil_de_la_question: d.seuil });
    return payload;
};

const genBoutiqueTrous = () => {
    const d = tirerBoutique();
    const iDonne = randInt(0, ARTICLES.length - 1);
    const phrase = ARTICLES.map((a, i) => (i === iDonne
        ? `${a} : ${d.ventes[i]} €`
        : `${a} : {{${d.ventes[i]}}} €`)).join('\n');
    const payload = makeTexteTrou(phrase,
        `${INTRO_BOUTIQUE}<p>Complète le tableau des ventes : une valeur est déjà écrite, `
        + 'lis les autres sur le diagramme.</p>',
        { ...opts(L_BOUTIQUE), mode: 'saisie', strict: false });
    payload.elements_additionnels = svgBoutique(d);
    payload._meta.params = paramsBoutique(d, { nombre_de_trous: ARTICLES.length - 1 });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « La piscine du quartier » (niveau 2 — encadrement)
// ═════════════════════════════════════════════════════════════

const CRENEAUX = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/** Chaque valeur vaut k × pas + reste avec 0 < reste < pas : aucun sommet de
 *  barre ne tombe sur une graduation, l'encadrement a donc une seule réponse. */
const tirerPiscine = () => {
    for (let essai = 0; essai < 80; essai++) {
        const k = randInt(4, 6);
        const pas = randomChoice([25, 50]);
        const restes = pas === 25 ? [5, 10, 15, 20] : [10, 20, 30, 40];
        const ks = shuffle([2, 3, 4, 5, 6, 7, 8]).slice(0, k);
        const vals = ks.map((kk) => kk * pas + randomChoice(restes));
        const nGrad = Math.max(...ks) + 1;
        if (nGrad < 5 || nGrad > 12) continue;
        return { k, pas, creneaux: CRENEAUX.slice(0, k), vals, ks, nGrad, yMax: nGrad * pas };
    }
    return { k: 5, pas: 50, creneaux: CRENEAUX.slice(0, 5), vals: [130, 170, 280, 330, 440],
        ks: [2, 3, 5, 6, 8], nGrad: 9, yMax: 450 };
};

const svgPiscine = (d) => imgElement(svgBarres(d.creneaux, d.vals,
    { yStep: d.pas, yLabel: 'Entrées', xLabel: 'Jour de la semaine' }));

const INTRO_PISCINE = '<p>Le diagramme donne le nombre d\'entrées d\'une piscine de quartier, '
    + 'jour par jour. Aucun sommet de barre ne tombe sur une graduation.</p>';

const intervalle = (pas, kk) => `entre ${kk * pas} et ${(kk + 1) * pas}`;

const paramsPiscine = (d, extra = {}) => ({
    niveau: 2,
    support_du_document: 'diagramme',
    famille_de_diagramme: 'diagramme en barres',
    nombre_de_modalites_du_document: d.k,
    valeur_representee: d.vals,
    pas_de_graduation_de_l_axe: d.pas,
    nombre_de_graduations_de_l_axe: d.nGrad,
    origine_de_l_axe_des_valeurs: 'zéro',
    position_de_la_valeur_par_rapport_aux_graduations: 'strictement entre deux graduations',
    nombre_de_questions_par_document: 2,
    ...extra,
});

const genPiscineQcm = () => {
    const d = tirerPiscine();
    const i = randInt(0, d.k - 1);
    const kc = d.ks[i];
    const bonne = intervalle(d.pas, kc);
    const dist = [
        { code: 'graduation_voisine_pour_encadrement', valeur: intervalle(d.pas, kc - 1) },
        { code: 'graduation_voisine_pour_encadrement', valeur: intervalle(d.pas, kc + 1) },
        { code: 'graduation_voisine_pour_encadrement', valeur: intervalle(d.pas, kc - 2) },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${INTRO_PISCINE}<p>Entre quelles deux graduations se situe le nombre d'entrées du ${d.creneaux[i]} ?</p>`,
        opts(L_PISCINE));
    payload.elements_additionnels = svgPiscine(d);
    payload._meta.params = paramsPiscine(d, { distracteurs: dist });
    return payload;
};

const genPiscineVF = () => {
    const d = tirerPiscine();
    const idx = shuffle(d.vals.map((_, i) => i)).slice(0, 4);
    const vs = equilibrer(idx.map(() => randInt(0, 1) === 0));
    const codes = [];
    const lignes = idx.map((i, n) => {
        const decalage = randInt(0, 1) === 0 ? 1 : -1;
        const kk = vs[n] ? d.ks[i] : d.ks[i] + decalage;
        if (!vs[n]) codes.push('graduation_voisine_pour_encadrement');
        return [`Le nombre d'entrées du ${d.creneaux[i]} est compris ${intervalle(d.pas, kk)}.`, vs[n] ? 0 : 1];
    });
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${INTRO_PISCINE}<p>Vrai ou faux ?</p>`,
        { ...opts(L_PISCINE), elements_additionnels: svgPiscine(d) });
    payload._meta.params = paramsPiscine(d, { distracteurs: codes.map((code) => ({ code })) });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Les clubs du collège » (niveau 3 — tableau à double entrée)
// ═════════════════════════════════════════════════════════════

const NIVEAUX_COLLEGE = ['6e', '5e', '4e', '3e'];
const CLUBS = ['Théâtre', 'Échecs', 'Robotique'];

/** Toutes les cases, tous les totaux de ligne, de colonne et le total général
 *  sont DEUX À DEUX DISTINCTS : un distracteur pris dans le tableau ne peut
 *  jamais coïncider avec la bonne réponse. */
const tirerCroise = () => {
    for (let essai = 0; essai < 120; essai++) {
        const cells = NIVEAUX_COLLEGE.map(() => CLUBS.map(() => randInt(4, 60)));
        const totL = cells.map((r) => r.reduce((a, b) => a + b, 0));
        const totC = CLUBS.map((_, j) => cells.reduce((a, r) => a + r[j], 0));
        const grand = totL.reduce((a, b) => a + b, 0);
        const tous = [...cells.flat(), ...totL, ...totC, grand];
        if (new Set(tous).size !== tous.length) continue;
        return { cells, totL, totC, grand };
    }
    return {
        cells: [[12, 25, 31], [18, 9, 44], [7, 33, 21], [15, 28, 5]],
        totL: [68, 71, 61, 48], totC: [52, 95, 101], grand: 248,
    };
};

const introCroise = (d) => '<p>Le tableau donne le nombre d\'élèves inscrits dans chaque club '
    + 'd\'un collège, niveau par niveau.</p>'
    + tableEnonce(
        ligneTableEntete(['Niveau', ...CLUBS, 'Total']),
        ...NIVEAUX_COLLEGE.map((n, i) => ligneTable(n, [...d.cells[i], d.totL[i]])),
        ligneTable('Total', [...d.totC, d.grand]),
    );

const paramsCroise = (d, extra = {}) => ({
    niveau: 3,
    support_du_document: 'tableau',
    nombre_de_modalites_du_document: NIVEAUX_COLLEGE.length + CLUBS.length,
    valeur_representee: [...d.cells.flat(), ...d.totL, ...d.totC, d.grand],
    nombre_de_questions_par_document: 3,
    ...extra,
});

const genCroiseSaisie = () => {
    const d = tirerCroise();
    const i = randInt(0, NIVEAUX_COLLEGE.length - 1);
    const j = randInt(0, CLUBS.length - 1);
    const payload = makeSaisie(String(d.cells[i][j]),
        `${introCroise(d)}<p>Combien d'élèves de ${NIVEAUX_COLLEGE[i]} sont inscrits au club `
        + `${CLUBS[j]} ?</p>`,
        { ...opts(L_CROISE), suffixe: 'élèves' });
    payload._meta.params = paramsCroise(d, { erreurVisee: 'ligne_pour_colonne' });
    return payload;
};

const genCroiseQcm = () => {
    const d = tirerCroise();
    const i = randInt(0, NIVEAUX_COLLEGE.length - 1);
    const j = randInt(0, CLUBS.length - 1);
    const iAutre = randomChoice(NIVEAUX_COLLEGE.map((_, x) => x).filter((x) => x !== i));
    const jAutre = randomChoice(CLUBS.map((_, x) => x).filter((x) => x !== j));
    const bonne = String(d.cells[i][j]);
    const dist = [
        { code: 'ligne_pour_colonne', valeur: String(d.cells[iAutre][j]) },
        { code: 'ligne_pour_colonne', valeur: String(d.cells[i][jAutre]) },
        { code: 'ligne_pour_colonne', valeur: String(d.totL[i]) },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${introCroise(d)}<p>Combien d'élèves de ${NIVEAUX_COLLEGE[i]} sont inscrits au club `
        + `${CLUBS[j]} ?</p>`,
        opts(L_CROISE));
    payload._meta.params = paramsCroise(d, { distracteurs: dist });
    return payload;
};

const genCroiseVF = () => {
    const d = tirerCroise();
    const i = randInt(0, NIVEAUX_COLLEGE.length - 1);
    const j = randInt(0, CLUBS.length - 1);
    const iAutre = randomChoice(NIVEAUX_COLLEGE.map((_, x) => x).filter((x) => x !== i));
    const jMax = d.totC.indexOf(Math.max(...d.totC));
    const jMin = d.totC.indexOf(Math.min(...d.totC));
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('ligne_pour_colonne');
    if (!vs[2]) codes.push('extremum_inverse');
    const lignes = [
        [`${vs[0] ? d.cells[i][j] : d.cells[iAutre][j]} élèves de ${NIVEAUX_COLLEGE[i]} sont inscrits au club ${CLUBS[j]}.`, vs[0] ? 0 : 1],
        [`En tout, ${vs[1] ? d.totL[i] : d.totL[iAutre]} élèves de ${NIVEAUX_COLLEGE[i]} sont inscrits dans un club.`, vs[1] ? 0 : 1],
        [`Le club qui rassemble le plus d'élèves du collège est le club ${vs[2] ? CLUBS[jMax] : CLUBS[jMin]}.`, vs[2] ? 0 : 1],
        [`Le collège compte ${vs[3] ? d.grand : d.grand + 10} inscriptions dans les clubs.`, vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${introCroise(d)}<p>Vrai ou faux ?</p>`, opts(L_CROISE));
    payload._meta.params = paramsCroise(d, { distracteurs: codes.map((code) => ({ code })) });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « La croissance du plant » (niveau 3 — graphique cartésien)
// ═════════════════════════════════════════════════════════════

const SEMAINES = [0, 1, 2, 3, 4, 5];
const PAS_PLANT = 10;

/** Hauteurs multiples de 10 (donc sur les graduations), croissantes ; la plus
 *  forte hausse dépasse toutes les autres d'au moins DEUX pas de graduation. */
const tirerPlant = () => {
    for (let essai = 0; essai < 120; essai++) {
        const h0 = randomChoice([10, 20]);
        const inc = [0, 0, 0, 0, 0].map(() => randomChoice([10, 20, 30, 40]));
        const tri = [...inc].sort((a, b) => b - a);
        if (tri[0] - tri[1] < 2 * PAS_PLANT) continue;
        const h = [h0];
        for (let i = 0; i < 5; i++) h.push(h[i] + inc[i]);
        if (h[5] < 50 || h[5] > 120) continue;
        return { h, inc, iMax: inc.indexOf(tri[0]), iMin: inc.lastIndexOf(tri[4]), nGrad: h[5] / PAS_PLANT };
    }
    const inc = [10, 10, 40, 10, 10];
    return { h: [10, 20, 30, 70, 80, 90], inc, iMax: 2, iMin: 4, nGrad: 9 };
};

const svgPlant = (d) => imgElement(svgLigneBrisee(SEMAINES.map(String), d.h,
    { yStep: PAS_PLANT, yLabel: 'Hauteur (cm)', xLabel: 'Temps (semaines)' }));

const INTRO_PLANT = '<p>Un élève mesure chaque semaine la hauteur d\'un plant de haricot. '
    + 'Le graphique porte le temps, en semaines, sur l\'axe horizontal, et la hauteur, '
    + 'en centimètres, sur l\'axe vertical.</p>';

const periode = (i) => `de la semaine ${i} à la semaine ${i + 1}`;

const paramsPlant = (d, extra = {}) => ({
    niveau: 3,
    support_du_document: 'graphique cartésien',
    famille_de_diagramme: 'diagramme en ligne brisée',
    nombre_de_modalites_du_document: SEMAINES.length,
    valeur_representee: d.h,
    pas_de_graduation_de_l_axe: PAS_PLANT,
    nombre_de_graduations_de_l_axe: d.nGrad,
    origine_de_l_axe_des_valeurs: 'zéro',
    position_de_la_valeur_par_rapport_aux_graduations: 'sur une graduation',
    ecart_minimal_entre_deux_valeurs_comparees: 2,
    nombre_de_questions_par_document: 3,
    ...extra,
});

const genPlantSaisie = () => {
    const d = tirerPlant();
    if (randInt(0, 1) === 0) {
        const k = randInt(1, 5);
        const payload = makeSaisie(String(d.h[k]),
            `${INTRO_PLANT}<p>Quelle était la hauteur du plant à la fin de la semaine ${k} ?</p>`,
            { ...opts(L_PLANT), suffixe: 'cm' });
        payload.elements_additionnels = svgPlant(d);
        payload._meta.params = paramsPlant(d, { erreurVisee: 'axes_inverses', sens_de_lecture: 'abscisse vers ordonnée' });
        return payload;
    }
    const k = randInt(1, 5);
    const payload = makeSaisie(String(k),
        `${INTRO_PLANT}<p>À la fin de quelle semaine le plant mesurait-il ${d.h[k]} cm ? `
        + 'Donne le numéro de la semaine.</p>',
        { ...opts(L_PLANT) });
    payload.elements_additionnels = svgPlant(d);
    payload._meta.params = paramsPlant(d, { erreurVisee: 'axes_inverses', sens_de_lecture: 'ordonnée vers abscisse' });
    return payload;
};

const genPlantQcm = () => {
    const d = tirerPlant();
    const bonne = periode(d.iMax);
    const inverse = periode(d.iMin);
    const autres = shuffle([0, 1, 2, 3, 4].filter((i) => i !== d.iMax && i !== d.iMin))
        .slice(0, 2).map(periode);
    const payload = makeQcm([bonne, inverse, ...autres], bonne,
        `${INTRO_PLANT}<p>Entre quelles deux semaines consécutives le plant a-t-il le plus grandi ? `
        + 'C\'est là que le segment du graphique monte le plus vite.</p>',
        opts(L_PLANT));
    payload.elements_additionnels = svgPlant(d);
    payload._meta.params = paramsPlant(d, {
        distracteurs: [{ code: 'extremum_inverse', valeur: inverse }],
    });
    return payload;
};

const genPlantVF = () => {
    const d = tirerPlant();
    const k = randInt(1, 5);
    const i = randInt(0, 4);
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('axes_inverses');
    if (!vs[1]) codes.push('extremum_inverse');
    if (!vs[3]) codes.push('axes_inverses');
    const lignes = [
        [`À la fin de la semaine ${k}, le plant mesurait ${vs[0] ? d.h[k] : k} cm.`, vs[0] ? 0 : 1],
        [`C'est ${vs[1] ? periode(d.iMax) : periode(d.iMin)} que le plant a le plus grandi.`, vs[1] ? 0 : 1],
        [`Le plant a grandi de ${vs[2] ? d.inc[i] : d.inc[i] + 10} cm ${periode(i)}.`, vs[2] ? 0 : 1],
        [vs[3]
            ? 'Sur ce graphique, l\'axe horizontal porte le temps et l\'axe vertical la hauteur.'
            : 'Sur ce graphique, l\'axe horizontal porte la hauteur et l\'axe vertical le temps.', vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${INTRO_PLANT}<p>Vrai ou faux ?</p>`,
        { ...opts(L_PLANT), elements_additionnels: svgPlant(d) });
    payload._meta.params = paramsPlant(d, { distracteurs: codes.map((code) => ({ code })) });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Paul et la télévision » (niveau 4 — axe tronqué)
// ═════════════════════════════════════════════════════════════

const CATS_TV = ['jamais', 'parfois', 'souvent', 'très souvent', 'toujours'];
const PAS_TV = 5;
const Y_MIN_TV = 15;

/** « parfois » = p, « souvent » = 2p EXACTEMENT, « très souvent » = 0 ;
 *  le diagramme A est tronqué à 15, le diagramme B part de 0. */
const tirerTv = () => {
    const p = randomChoice([20, 25]);
    const reste = [20, 25, 30, 35, 45].filter((v) => v !== p && v !== 2 * p);
    const [jamais, toujours] = shuffle(reste).slice(0, 2);
    const effs = [jamais, p, 2 * p, 0, toujours];
    const hautMax = Math.max(...effs);
    return { p, effs, nGradA: (hautMax - Y_MIN_TV) / PAS_TV, nGradB: hautMax / PAS_TV };
};

const svgTv = (d) => deuxImages(
    svgBarres(CATS_TV, d.effs, { yStep: PAS_TV, yMin: Y_MIN_TV, yLabel: 'Effectifs', xLabel: 'Diagramme A' }),
    svgBarres(CATS_TV, d.effs, { yStep: PAS_TV, yLabel: 'Effectifs', xLabel: 'Diagramme B' }),
);

const INTRO_TV = '<p>On a demandé à des personnes à quelle fréquence elles regardent la télévision '
    + 'à la pause de midi. Les diagrammes A et B représentent la même série : seule l\'origine '
    + 'de l\'axe des effectifs change. L\'axe du diagramme A ne part pas de 0.</p>';

const paramsTv = (d, extra = {}) => ({
    niveau: 4,
    support_du_document: 'diagramme',
    famille_de_diagramme: 'diagramme en barres',
    nombre_de_modalites_du_document: CATS_TV.length,
    valeur_representee: d.effs,
    pas_de_graduation_de_l_axe: PAS_TV,
    nombre_de_graduations_de_l_axe: d.nGradA,
    origine_de_l_axe_des_valeurs: 'tronquée',
    position_de_la_valeur_par_rapport_aux_graduations: 'sur une graduation',
    ecart_minimal_entre_deux_valeurs_comparees: 2,
    nombre_de_questions_par_document: 3,
    ...extra,
});

const genTvTrous = () => {
    const d = tirerTv();
    const iDonne = randomChoice([0, 1, 2, 4]);
    const phrase = CATS_TV.map((c, i) => (i === iDonne
        ? `${c} : ${d.effs[i]}`
        : `${c} : {{${d.effs[i]}}}`)).join('\n');
    const payload = makeTexteTrou(phrase,
        `${INTRO_TV}<p>Lis sur le diagramme B l'effectif de chaque réponse : une valeur est donnée, `
        + 'complète les autres. Une réponse peut n\'avoir été choisie par personne.</p>',
        { ...opts(L_TV), mode: 'saisie', strict: false });
    payload.elements_additionnels = svgTv(d);
    payload._meta.params = paramsTv(d, { nombre_de_trous: CATS_TV.length - 1 });
    return payload;
};

const genTvSaisie = () => {
    const d = tirerTv();
    if (randInt(0, 1) === 0) {
        const payload = makeSaisie('0',
            `${INTRO_TV}<p>Combien de personnes ont répondu « très souvent » ?</p>`,
            { ...opts(L_TV), suffixe: 'personnes' });
        payload.elements_additionnels = svgTv(d);
        payload._meta.params = paramsTv(d, { question: 'catégorie sans barre' });
        return payload;
    }
    const i = randomChoice([0, 1, 2, 4]);
    const payload = makeSaisie(String(d.effs[i]),
        `${INTRO_TV}<p>Combien de personnes ont répondu « ${CATS_TV[i]} » ?</p>`,
        { ...opts(L_TV), suffixe: 'personnes' });
    payload.elements_additionnels = svgTv(d);
    payload._meta.params = paramsTv(d, { question: 'lecture exacte sur le diagramme B' });
    return payload;
};

const genTvQcm = () => {
    const d = tirerTv();
    if (randInt(0, 1) === 0) {
        const bonne = `Oui : ${2 * d.p} = 2 × ${d.p}`;
        const dist = [
            { code: 'axe_tronque_ecart_exagere', valeur: 'Non : sur le diagramme A, la barre « souvent » est bien plus de deux fois plus haute' },
            { code: 'axe_tronque_ecart_exagere', valeur: 'Non : sur le diagramme A, l\'écart entre les deux barres est énorme' },
            { code: 'axe_tronque_ecart_exagere', valeur: 'Non : le diagramme A montre environ quatre fois plus de personnes' },
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `${INTRO_TV}<p>Paul affirme : « Il y a deux fois plus de personnes qui regardent souvent la télévision `
            + 'que de personnes qui la regardent parfois. » A-t-il raison ?</p>',
            opts(L_TV));
        payload.elements_additionnels = svgTv(d);
        payload._meta.params = paramsTv(d, { distracteurs: dist });
        return payload;
    }
    const bonne = 'L\'axe des effectifs du diagramme A ne commence pas à 0 : les hauteurs n\'y sont pas proportionnelles aux effectifs';
    const dist = [
        { code: 'axe_tronque_ecart_exagere', valeur: 'Les barres du diagramme A n\'ont pas toutes la même largeur' },
        { code: 'axe_tronque_ecart_exagere', valeur: 'Le diagramme A est dessiné en perspective' },
        { code: 'axe_tronque_ecart_exagere', valeur: 'Les effectifs du diagramme A sont faux' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${INTRO_TV}<p>« Souvent » (${2 * d.p} personnes) vaut exactement le double de « parfois » `
        + `(${d.p} personnes). Pourtant, sur le diagramme A, la barre « souvent » semble bien plus `
        + 'de deux fois plus haute. Qu\'est-ce que cette présentation exagère, et pourquoi ?</p>',
        opts(L_TV));
    payload.elements_additionnels = svgTv(d);
    payload._meta.params = paramsTv(d, { distracteurs: dist });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Tom et les deux villes » (niveau 4 — parts contre effectifs)
// ═════════════════════════════════════════════════════════════

const CATS_SPORTS = ['sports collectifs', 'sports de raquette', 'sports de combat', 'autres sports'];

/** La part des sports collectifs est PLUS PETITE dans Bêta que dans Alpha,
 *  et l'effectif y est pourtant PLUS GRAND (écart d'au moins 20 licenciés). */
const tirerVilles = () => {
    for (let essai = 0; essai < 120; essai++) {
        const pctA = randomChoice([25, 30]);
        const pctB = randomChoice([15, 20]);
        const totalA = randomChoice([400, 500, 600]);
        const totalB = randomChoice([700, 800, 900]);
        const effA = (pctA * totalA) / 100;
        const effB = (pctB * totalB) / 100;
        if (effB - effA < 20) continue;
        const completer = (p1) => {
            const p2 = randomChoice([15, 20, 25]);
            const p3 = randomChoice([15, 20]);
            const p4 = 100 - p1 - p2 - p3;
            return p4 >= 10 ? [p1, p2, p3, p4] : null;
        };
        const partsA = completer(pctA);
        const partsB = completer(pctB);
        if (!partsA || !partsB) continue;
        return { pctA, pctB, totalA, totalB, effA, effB, partsA, partsB };
    }
    return { pctA: 25, pctB: 20, totalA: 400, totalB: 900, effA: 100, effB: 180,
        partsA: [25, 25, 20, 30], partsB: [20, 20, 15, 45] };
};

const svgVilles = (d) => {
    const fait = (parts, ville, total) => svgCamembert(
        CATS_SPORTS.map((nom, i) => ({ nom, pct: parts[i] })),
        { titre: `ville ${ville}`, sousTitre: `${total} licenciés`, labelsSurSecteurs: true });
    return deuxImages(fait(d.partsA, 'Alpha', d.totalA), fait(d.partsB, 'Bêta', d.totalB));
};

const INTRO_VILLES = '<p>Les diagrammes circulaires donnent la répartition des sportifs licenciés '
    + 'de deux villes. Le nombre total de licenciés est écrit sous le nom de chaque ville.</p>';

const paramsVilles = (d, extra = {}) => ({
    niveau: 4,
    support_du_document: 'diagramme',
    famille_de_diagramme: 'diagramme circulaire',
    nombre_de_modalites_du_document: CATS_SPORTS.length,
    valeur_representee: [...d.partsA, ...d.partsB],
    effectif_total_de_chaque_document: [d.totalA, d.totalB],
    nombre_de_questions_par_document: 3,
    ...extra,
});

const genVillesTrous = () => {
    const d = tirerVilles();
    const phrase = `Ville Alpha : {{${d.effA}}} licenciés\nVille Bêta : {{${d.effB}}} licenciés`;
    const payload = makeTexteTrou(phrase,
        `${INTRO_VILLES}<p>Calcule le nombre de licenciés en sports collectifs de chaque ville : `
        + `${d.pctA} % des ${d.totalA} licenciés de la ville Alpha, ${d.pctB} % des ${d.totalB} `
        + 'licenciés de la ville Bêta.</p>',
        { ...opts(L_VILLES), mode: 'saisie', strict: false });
    payload.elements_additionnels = svgVilles(d);
    payload._meta.params = paramsVilles(d, { nombre_de_trous: 2 });
    return payload;
};

const genVillesQcm = () => {
    const d = tirerVilles();
    if (randInt(0, 1) === 0) {
        const bonne = `Non : ${d.pctB} % de ${d.totalB}, cela fait ${d.effB} licenciés, donc plus que les ${d.effA} de la ville Alpha`;
        const dist = [
            { code: 'parts_pour_effectifs', valeur: `Oui : ${d.pctB} % est plus petit que ${d.pctA} %, donc il y a moins de pratiquants dans la ville Bêta` },
            { code: 'parts_pour_effectifs', valeur: 'Oui : le secteur de la ville Bêta est plus petit sur le dessin' },
            { code: 'parts_pour_effectifs', valeur: 'Oui : deux diagrammes circulaires se comparent directement, secteur par secteur' },
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `${INTRO_VILLES}<p>Tom affirme : « Il y a moins de pratiquants de sports collectifs dans la ville Bêta, `
            + `car ${d.pctB} % est plus petit que ${d.pctA} %. » A-t-il raison ?</p>`,
            opts(L_VILLES));
        payload.elements_additionnels = svgVilles(d);
        payload._meta.params = paramsVilles(d, { distracteurs: dist });
        return payload;
    }
    const bonne = 'Les effectifs totaux, c\'est-à-dire le nombre de licenciés de chaque ville';
    const dist = [
        { code: 'parts_pour_effectifs', valeur: 'Rien : les pourcentages suffisent toujours à comparer deux villes' },
        { code: 'parts_pour_effectifs', valeur: 'Rien : le secteur le plus grand donne toujours l\'effectif le plus grand' },
        { code: 'parts_pour_effectifs', valeur: 'Rien : il suffit de comparer les aires des deux disques dessinés' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${INTRO_VILLES}<p>Un diagramme circulaire montre des parts, c'est-à-dire des pourcentages. `
        + 'Que faut-il connaître en plus des pourcentages pour comparer les effectifs de deux villes ?</p>',
        opts(L_VILLES));
    payload.elements_additionnels = svgVilles(d);
    payload._meta.params = paramsVilles(d, { distracteurs: dist });
    return payload;
};

const genVillesVF = () => {
    const d = tirerVilles();
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[1]) codes.push('parts_pour_effectifs');
    if (!vs[2]) codes.push('parts_pour_effectifs');
    if (!vs[3]) codes.push('parts_pour_effectifs');
    const lignes = [
        [`La ville Bêta compte ${vs[0] ? d.effB : d.effA} licenciés en sports collectifs.`, vs[0] ? 0 : 1],
        [`Il y a ${vs[1] ? 'plus' : 'moins'} de licenciés en sports collectifs dans la ville Bêta que dans la ville Alpha.`, vs[1] ? 0 : 1],
        [vs[2]
            ? 'Un pourcentage plus petit peut correspondre à un effectif plus grand.'
            : 'Un pourcentage plus petit correspond toujours à un effectif plus petit.', vs[2] ? 0 : 1],
        [vs[3]
            ? 'Pour comparer les effectifs de deux villes, les pourcentages ne suffisent pas.'
            : 'Pour comparer les effectifs de deux villes, les pourcentages suffisent.', vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${INTRO_VILLES}<p>Vrai ou faux ?</p>`,
        { ...opts(L_VILLES), elements_additionnels: svgVilles(d) });
    payload._meta.params = paramsVilles(d, { distracteurs: codes.map((code) => ({ code })) });
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    cinema_texte: genCinemaSaisie,
    cinema_qcm_unique: genCinemaQcm,
    cinema_tableau_vf: genCinemaVF,
    boutique_qcm_unique: genBoutiqueQcm,
    boutique_qcm_multiple: genBoutiqueQcmMulti,
    boutique_texte_trou_saisie: genBoutiqueTrous,
    piscine_qcm_unique: genPiscineQcm,
    piscine_tableau_vf: genPiscineVF,
    croise_texte: genCroiseSaisie,
    croise_qcm_unique: genCroiseQcm,
    croise_tableau_vf: genCroiseVF,
    plant_texte: genPlantSaisie,
    plant_qcm_unique: genPlantQcm,
    plant_tableau_vf: genPlantVF,
    tv_texte_trou_saisie: genTvTrous,
    tv_texte: genTvSaisie,
    tv_qcm_unique: genTvQcm,
    villes_texte_trou_saisie: genVillesTrous,
    villes_qcm_unique: genVillesQcm,
    villes_tableau_vf: genVillesVF,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'cinema', label: L_CINEMA, formatsList: buildFormats('cinema') },
    { id: 'boutique', label: L_BOUTIQUE, formatsList: buildFormats('boutique') },
    { id: 'piscine', label: L_PISCINE, formatsList: buildFormats('piscine') },
    { id: 'croise', label: L_CROISE, formatsList: buildFormats('croise') },
    { id: 'plant', label: L_PLANT, formatsList: buildFormats('plant') },
    { id: 'tv', label: L_TV, formatsList: buildFormats('tv') },
    { id: 'villes', label: L_VILLES, formatsList: buildFormats('villes') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
